import type { Messaging } from "firebase/messaging";
import { Logger } from "@/lib/logger";
import { FIREBASE_CONFIG, FIREBASE_VAPID_KEY } from "@/lib/push/firebase-config";
import { API_BASE_URL } from "@/services/api";
import { STORAGE_KEYS } from "@/lib/storage-keys";

const log = new Logger("Push");

const SW_PATH = "/firebase-messaging-sw.js";

/**
 * `firebase/app` + `firebase/messaging` ne sont importés qu'à la demande — jamais depuis le bundle
 * initial (cf. CLAUDE.md, "Heavy deps"). `PushSubscriber`/`PushEnablePrompt` s'exécutent pour
 * *toute* session authentifiée dès `EchoProvider`, donc un import statique ici alourdirait le
 * premier chargement de chaque utilisateur, y compris ceux qui n'activeront jamais les
 * notifications ou dont le navigateur ne les supporte pas.
 */
async function loadFirebase() {
  const [{ initializeApp, getApps, getApp }, messagingSdk] = await Promise.all([
    import("firebase/app"),
    import("firebase/messaging"),
  ]);
  return { initializeApp, getApps, getApp, ...messagingSdk };
}

function firebaseApp(sdk: Awaited<ReturnType<typeof loadFirebase>>) {
  return sdk.getApps().length ? sdk.getApp() : sdk.initializeApp(FIREBASE_CONFIG);
}

let messagingInstance: Messaging | null | undefined;

/**
 * Attend qu'une registration ait un worker **actif** — condition que `register()` ne garantit pas :
 * sa promesse résout dès la création de la registration, pas une fois le cycle
 * installing → installed → activating → activated terminé. `registration.active` reste `null`
 * pendant toute cette fenêtre.
 *
 * `PushManager.subscribe()` (appelé en interne par `getToken()` de Firebase) exige un `active` non
 * nul et rejette sinon avec `AbortError: ... no active Service Worker` — observé en prod sur une
 * première activation des notifications juste après le chargement de la page (Sentry
 * #146405924) : `register()` avait résolu, mais le SW était encore en `installing`/`waiting`.
 *
 * `navigator.serviceWorker.ready` ne convient pas ici : elle attend que la registration **contrôle
 * la page courante**, ce qui n'arrive qu'après un rechargement pour un tout premier enregistrement
 * (avant `clients.claim()`, cf. `firebase-messaging-sw.js`) — elle resterait donc en attente
 * indéfiniment dans exactement le cas qu'on veut couvrir. On observe directement l'état du worker
 * concerné via `statechange`, avec un filet en cas de blocage anormal.
 *
 * Reboucle plutôt que de suivre un seul `ServiceWorker` du début à la fin : celui capturé en
 * `installing`/`waiting` peut devenir `redundant` sans jamais passer par `activated` — remplacé par
 * un worker plus récent avant d'avoir fini de s'installer. C'est exactement ce que produit
 * `registerServiceWorker()` juste au-dessus, qui appelle `existing.update()` en parallèle de cet
 * appel : la vérification de mise à jour peut découvrir un nouveau fichier et démarrer un second
 * install pendant qu'on observe encore le premier. Un `worker.state === "redundant"` non traité
 * laissait alors la promesse pendre jusqu'au filet de 10s, qui résolvait ensuite avec une
 * registration dont `.active` restait `null` — le même `AbortError: ... no active Service Worker`
 * que le correctif initial visait à éliminer, revenu par une autre voie (Sentry #146746893).
 * Sur `redundant`, on relit `registration.installing`/`.waiting` : soit un nouveau worker a pris le
 * relais (on l'observe à son tour), soit `registration.active` est déjà posé (on résout tout de
 * suite).
 */
function waitForActive(
  registration: ServiceWorkerRegistration,
): Promise<ServiceWorkerRegistration> {
  if (registration.active) return Promise.resolve(registration);

  return new Promise((resolve) => {
    let settled = false;
    const timeout = setTimeout(() => {
      settle();
    }, 10_000);

    function settle() {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      currentWorker?.removeEventListener("statechange", onStateChange);
      resolve(registration);
    }

    let currentWorker: ServiceWorker | null = null;

    function observe(worker: ServiceWorker | null) {
      currentWorker?.removeEventListener("statechange", onStateChange);
      currentWorker = worker;
      currentWorker?.addEventListener("statechange", onStateChange);
    }

    function onStateChange() {
      if (registration.active) {
        settle();
        return;
      }
      if (currentWorker?.state === "redundant") {
        const next = registration.installing || registration.waiting;
        if (next && next !== currentWorker) {
          observe(next);
        } else {
          // Plus rien en cours d'installation et toujours pas de `.active` : rien de plus à
          // attendre, la registration ne progressera pas seule.
          settle();
        }
      }
    }

    const initialWorker = registration.installing || registration.waiting;
    if (!initialWorker) {
      settle();
      return;
    }
    observe(initialWorker);
  });
}

/**
 * Le service worker FCM dédié, enregistré une fois et réutilisé — distinct de tout futur service
 * worker PWA (cf. §3.1 de `docs/PWA_PUSH_NOTIFICATIONS.md`).
 *
 * Force une vérification de mise à jour (`registration.update()`) quand une registration existe
 * déjà. Le navigateur revérifie de lui-même le fichier périodiquement, mais l'intervalle se compte
 * en heures — trop long pour une PWA installée qui reste ouverte en arrière-plan des jours durant
 * sans jamais recharger complètement. Cette fonction s'exécute à chaque démarrage de l'app et à
 * chaque login (cf. `usePushNotifications`) : autant de points où on sait qu'un déploiement a pu
 * avoir lieu entre-temps. `skipWaiting()`/`clients.claim()` côté SW (cf.
 * `firebase-messaging-sw.js`) font le reste : une fois la nouvelle version détectée, elle s'active
 * sans attendre la fermeture de l'app.
 */
async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (existing) {
      existing.update().catch(() => {
        // Best-effort : une vérification de mise à jour ratée (hors ligne, etc.) ne doit pas
        // empêcher d'utiliser la registration déjà active.
      });
      return await waitForActive(existing);
    }
    const registration = await navigator.serviceWorker.register(SW_PATH, { scope: "/" });
    return await waitForActive(registration);
  } catch (err) {
    log.warn("Échec d'enregistrement du service worker FCM", err);
    return null;
  }
}

/**
 * `Messaging`, initialisé au plus une fois. `null` sur un navigateur non supporté (Web Push absent
 * — typiquement iOS Safari hors PWA installée) ou hors contexte navigateur.
 */
export async function getMessagingInstance(): Promise<Messaging | null> {
  if (messagingInstance !== undefined) return messagingInstance;

  if (typeof window === "undefined") {
    messagingInstance = null;
    return messagingInstance;
  }

  const sdk = await loadFirebase();
  const supported = await sdk.isSupported().catch(() => false);
  if (!supported) {
    log.debug("Web Push non supporté sur ce navigateur.");
    messagingInstance = null;
    return messagingInstance;
  }

  messagingInstance = sdk.getMessaging(firebaseApp(sdk));
  return messagingInstance;
}

/**
 * Obtient (ou renouvelle) le jeton FCM Web de cet appareil.
 *
 * Suppose la permission déjà accordée (`Notification.permission === "granted"`) — c'est à
 * l'appelant de la demander explicitement au préalable, jamais ici en silence.
 */
export async function requestFcmToken(): Promise<string | null> {
  const messaging = await getMessagingInstance();
  if (!messaging) return null;

  const registration = await registerServiceWorker();
  if (!registration) return null;

  // `waitForActive` peut résoudre sans `.active` posé (filet de 10s, ou plus rien en cours
  // d'installation après un `redundant` — cf. sa documentation). `PushManager.subscribe()`
  // rejette alors systématiquement en `AbortError: ... no active Service Worker` : autant l'éviter
  // ici, dans un cas déjà identifié, plutôt que de laisser Firebase le découvrir en échouant.
  if (!registration.active) {
    log.warn("Service worker FCM toujours pas actif — jeton non demandé.");
    return null;
  }

  try {
    const { getToken } = await import("firebase/messaging");
    return await getToken(messaging, {
      vapidKey: FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
  } catch (err) {
    // `warn` plutôt que `error` : échec réseau tiers vers `fcmregistrations.googleapis.com`
    // (souvent transitoire — bloqueur de tiers, Private Relay Safari…), pas un bug applicatif.
    // Best-effort comme le reste de ce fichier : `requestFcmToken` retourne `null`, la
    // notification push est simplement différée à une prochaine tentative.
    log.warn("Échec de récupération du jeton FCM", err);
    return null;
  }
}

/** Invalide le jeton FCM local (best-effort, au logout). */
export async function clearFcmToken(): Promise<void> {
  const messaging = await getMessagingInstance();
  if (!messaging) return;
  try {
    const { deleteToken } = await import("firebase/messaging");
    await deleteToken(messaging);
  } catch (err) {
    log.warn("Échec de suppression du jeton FCM local", err);
  }
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // stockage bloqué : rien à nettoyer
  }
}

/**
 * Révoque le jeton push web de cet appareil (`DELETE /user/push-tokens/{id}`) et invalide le jeton
 * FCM local — à appeler **avant** l'invalidation de la session Sanctum (cf.
 * `docs/PWA_PUSH_NOTIFICATIONS.md` §3.3 : le back ne supprime pas ce jeton lui-même au logout, il
 * ne le connaît pas à ce moment).
 *
 * Prend le jeton Sanctum en argument plutôt que de le relire dans `useAuthStore` : appelée depuis
 * `logoutApi`, dont c'est déjà la convention (cf. les 4 `handleLogout` des layouts par rôle), et le
 * store a déjà remis son `token` à `null` au moment où cette requête part réellement. `fetchJson`
 * ne conviendrait donc pas ici — d'où un `fetch` brut, sur le même modèle que `logoutApi` lui-même.
 *
 * Best-effort et non bloquant : un échec ici ne doit jamais retarder ni empêcher la déconnexion.
 * Ne charge le SDK Firebase (`clearFcmToken`) que si un jeton a réellement été enregistré un jour —
 * sinon rien à invalider, et la majorité des logouts (utilisateur n'ayant jamais activé les push)
 * n'a aucune raison de télécharger le SDK.
 */
export async function revokePushTokenOnLogout(token: string): Promise<void> {
  const id = safeGet(STORAGE_KEYS.PUSH_TOKEN_ID);
  if (!id) return;

  try {
    await fetch(`${API_BASE_URL}/v1/user/push-tokens/${id}`, {
      method: "DELETE",
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    });
  } catch (err) {
    log.warn("Échec de révocation du jeton push au logout", err);
  }
  safeRemove(STORAGE_KEYS.PUSH_TOKEN_ID);
  safeRemove(STORAGE_KEYS.PUSH_TOKEN_VALUE);
  await clearFcmToken();
}

/**
 * Écoute les push reçus **au premier plan** (`onMessage`) — le service worker ne gère que
 * l'arrière-plan (`onBackgroundMessage`), les deux ne se déclenchent jamais pour le même message.
 * Retourne un désabonnement, ou `null` si la messagerie n'est pas disponible.
 */
export async function listenForegroundMessages(
  onReceive: (payload: { title?: string; body?: string; data?: Record<string, string> }) => void,
): Promise<(() => void) | null> {
  const messaging = await getMessagingInstance();
  if (!messaging) return null;

  const { onMessage } = await import("firebase/messaging");
  return onMessage(messaging, (payload) => {
    onReceive({
      title: payload.notification?.title,
      body: payload.notification?.body,
      data: payload.data,
    });
  });
}
